// Refund-amount math shared by the agent UI and the server (2026-10-14): the
// task flows' Confirm Refund Value check (server/src/ai/task-flows.js)
// compares a return document's refund total with the same per-line amounts
// the UI shows. Moved here unchanged from ui/src/refundAmount.js and
// ui/src/itemTax.js, which re-export them.

// computeUnitRefundAmounts() — per-unit refund amount for each line, mirrored
// from "RefundAmountCalcs.xlsx". A standalone or kit line refunds at its own
// unitPrice. A component line has no price of its own (TRQ carries a kit's
// full price on the kit line only) — it inherits a share of its PARENT kit's
// unitPrice, proportional to its own share of the kit's unitCost:
//   componentUnitRefundAmt = parentKit.unitPrice * (component.unitCost / parentKit.unitCost)
//
// Parent-kit join: same lineSequence + itemType === 'kit' sibling lookup
// SuggestionPanel.jsx's isComponentBlockedByKit()/toggleKitAwareSelection()
// and return-refund.js's isPartialSelection() already use — NOT itemTax.js's
// positional "walk items in order, tracking the current kit" approach (that
// only works because TRQ happens to keep a kit's components immediately
// after it; the lineSequence join doesn't depend on ordering).
//
// Returns an array parallel to `items`. null for a component whose parent
// kit can't be resolved, or whose parent's own unitCost is 0/null/absent
// (the ratio above would divide by zero) — never a fabricated guess.
export function computeUnitRefundAmounts(items) {
  if (!Array.isArray(items)) return []
  const kitsByLineSequence = new Map()
  for (const it of items) {
    if (it.itemType === 'kit' && it.lineSequence != null) kitsByLineSequence.set(it.lineSequence, it)
  }
  return items.map((it) => {
    if (it.itemType !== 'component') return Number(it.unitPrice) || 0
    const parentKit = it.lineSequence != null ? kitsByLineSequence.get(it.lineSequence) : null
    if (!parentKit) return null
    const kitUnitCost = Number(parentKit.unitCost) || 0
    if (!kitUnitCost) return null
    const kitUnitPrice = Number(parentKit.unitPrice) || 0
    const componentUnitCost = Number(it.unitCost) || 0
    return kitUnitPrice * (componentUnitCost / kitUnitCost)
  })
}

// Pure per-line sales-tax allocation for the All Items modal's "Tax" column —
// mirrored cell-by-cell from the reference workbook ("sales tax
// calculations.xlsx"). orderTax/shippingCost/orderTotal are the workbook's
// gray-cell (order-level) inputs; everything below is computed.
//
// A component line has no join key of its own back to its parent kit — TRQ's
// own item ordering is the only relationship (a component always belongs to
// the kit/standalone line immediately above it), so this must walk `items`
// in order, tracking the current kit as it goes, exactly like the workbook's
// own row layout.
//
// Returns an array parallel to `items` — items[i]'s line tax at result[i].
// null wherever the inputs needed for that item's own line aren't resolvable
// (missing orderTax/orderTotal, a zero orderSubtotal/kitExtendedCost, or a
// component with no preceding kit/standalone) — never a fabricated 0.
export function computeLineTax(items, { orderTax, shippingCost, orderTotal } = {}) {
  if (!Array.isArray(items)) return []
  if (orderTax == null || orderTotal == null) return items.map(() => null)
  const orderSubtotal = orderTotal - orderTax - (shippingCost ?? 0)

  let currentKit = null
  let currentKitLineTax = null

  return items.map((it) => {
    if (it.itemType === 'component') {
      if (!currentKit || currentKitLineTax == null) return null
      const kitExtendedCost = (currentKit.unitCost ?? 0) * (currentKit.quantity ?? 0)
      if (!kitExtendedCost) return null
      const pctOfKit = (it.unitCost ?? 0) / kitExtendedCost
      const componentUnitTax = pctOfKit * currentKitLineTax
      return componentUnitTax * (it.quantity ?? 0)
    }

    // kit or standalone
    currentKit = it
    if (!orderSubtotal) { currentKitLineTax = null; return null }
    const extendedPrice = (it.unitPrice ?? 0) * (it.quantity ?? 0)
    const pctOfOrder = extendedPrice / orderSubtotal
    const lineTax = pctOfOrder * orderTax
    currentKitLineTax = lineTax
    return lineTax
  })
}

// estimatedRefundBreakdown() — the items+tax refund for exactly the selected
// items ({ sku, quantity }), moved here unchanged from
// ui/src/SuggestionPanel.jsx (2026-10-21) so the server computes the same
// Original Refund the agent sees after Check, without Check
// (server/src/ai/refund-amount-fill.js). computeLineTax() is walked over the
// FULL allItems list (it needs kit/component order to split a kit's tax among
// its components), then only the selected lines' own item cost (at the
// SELECTED quantity) + tax are summed. The per-item cost uses
// computeUnitRefundAmounts(), so a selected component refunds at its share of
// its parent kit's unitPrice. deduction is the label cost to subtract (0 when
// nothing is deducted).
//
// unresolvedSkus: the selected line(s) whose Unit Refund Amt couldn't be
// resolved (computeUnitRefundAmounts returned null) — excluded from
// itemsTotal/estimatedRefund rather than counted as $0, so the caller can
// name them.
export function estimatedRefundBreakdown({ allItems, selectedItems, lineTaxes, deduction }) {
  const unitRefundAmts = computeUnitRefundAmounts(allItems)
  let itemsTotal = 0
  const unresolvedSkus = []
  ;(allItems ?? []).forEach((it, idx) => {
    const entry = (selectedItems ?? []).find((s) => s.sku === it.sku)
    if (!entry) return
    const unitRefundAmt = unitRefundAmts[idx]
    if (unitRefundAmt == null) {
      if (it.sku) unresolvedSkus.push(it.sku)
      return
    }
    itemsTotal += unitRefundAmt * (Number(entry.quantity) || 0)
    itemsTotal += lineTaxes?.[idx] ?? 0
  })
  const deductionAmount = Number(deduction) || 0
  return { itemsTotal, deductionAmount, estimatedRefund: itemsTotal - deductionAmount, unresolvedSkus }
}
